import { authenticatedRequest } from "./auth";

export type StsTokenResponse = {
  tmpSecretId: string;
  tmpSecretKey: string;
  sessionToken: string;
  expiration: string;
  expiredTime: number;
  bucket: string;
  region: string;
  appId: string;
  allowPrefix: string;
  suggestedPath: string;
  cdnDomain: string | null;
};

export type CosUploadInput = {
  blob: Blob;
  fileName: string;
  mimeType: string;
  onProgress?: (progress: number) => void;
  sourceKind: "trimmed" | "original_fallback";
  teacherToken: string;
};

export type CosUploadResult = {
  bucket: string;
  cdnDomain: string | null;
  objectKey: string;
  region: string;
};

export async function uploadVideoToCos({
  blob,
  fileName,
  mimeType,
  onProgress,
  sourceKind,
  teacherToken,
}: CosUploadInput): Promise<CosUploadResult> {
  const sts = await authenticatedRequest<StsTokenResponse>(`teacher-web/links/${encodeURIComponent(teacherToken)}/upload-token`, {
    method: "POST",
    body: {
      fileSize: blob.size,
      fileName,
      mimeType: mimeType || blob.type || "application/octet-stream",
      sourceKind,
    },
  });

  const objectKey = sts.suggestedPath;
  await putObjectWithOfficialSdk({
    blob,
    objectKey,
    sts,
    mimeType: mimeType || blob.type || "application/octet-stream",
    onProgress,
  });

  return {
    bucket: sts.bucket,
    cdnDomain: sts.cdnDomain,
    objectKey,
    region: sts.region,
  };
}

async function putObjectWithOfficialSdk({
  blob,
  mimeType,
  objectKey,
  onProgress,
  sts,
}: {
  blob: Blob;
  mimeType: string;
  objectKey: string;
  onProgress?: (progress: number) => void;
  sts: StsTokenResponse;
}): Promise<void> {
  const COS = await loadCosSdk();
  const now = Math.floor(Date.now() / 1000);
  const startTime = Math.max(0, now - 60);

  const cos = new COS({
    getAuthorization: (_options, callback) => {
      callback({
        TmpSecretId: sts.tmpSecretId,
        TmpSecretKey: sts.tmpSecretKey,
        SecurityToken: sts.sessionToken,
        StartTime: startTime,
        ExpiredTime: sts.expiredTime,
      });
    },
  });

  await new Promise<void>((resolve, reject) => {
    cos.putObject(
      {
        Body: blob,
        Bucket: sts.bucket,
        ContentType: mimeType || "application/octet-stream",
        Key: objectKey,
        Region: sts.region,
        onProgress: (progressData) => {
          if (typeof progressData.percent === "number") {
            onProgress?.(Math.round(progressData.percent * 100));
          }
        },
      },
      (error) => {
        if (error) {
          reject(new Error(normalizeCosUploadError(error)));
          return;
        }
        onProgress?.(100);
        resolve();
      }
    );
  });
}

let cosSdkPromise: Promise<CosConstructor> | null = null;

async function loadCosSdk(): Promise<CosConstructor> {
  cosSdkPromise ??= import("cos-js-sdk-v5/src/cos.js").then((module) => {
    const maybeDefault = module.default ?? module;
    return maybeDefault as unknown as CosConstructor;
  });

  return cosSdkPromise;
}

function normalizeCosUploadError(error: unknown): string {
  if (error instanceof Error && error.message) {
    return `COS 上传失败：${error.message}`;
  }

  if (typeof error === "object" && error !== null) {
    const record = error as Record<string, unknown>;
    const code = typeof record.Code === "string" ? record.Code : undefined;
    const message = typeof record.Message === "string" ? record.Message : undefined;
    if (code || message) {
      return `COS 上传失败：${[code, message].filter(Boolean).join(" - ")}`;
    }
  }

  return "COS 上传失败";
}
